// --------------------------------------------------------------
// Created On: 2024-05-08
// Author: KC Willard
//
// Last Modified: 2025-11-19
// Modified By: Marco Serrano
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import { API } from "../../constants/miscellaneous";
import useApi from "../../hooks/useApi";
import isoToTimePassed from "../../utilities/time/isoToTimePassed";
import isoUtcAddDays from "../../utilities/time/isoUtcAddDays";
import isPacificDaylightTime from "../../utilities/time/isPacificDaylightTime";
import DotLoader from "../DotLoader/DotLoader";
import styles from "./LeadTime.module.scss";
import addBusinessDays from "src/utilities/time/addBusinessDays";

// Component that makes an api call to retrieve the lead time for the given part number and quantity.
export default function LeadTime(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [leadTime, setLeadTime] = useState<string>("");

  const partHasPaintColor: boolean = props.paintColorId !== null;
  const partHasModifiedImpellerAngle: boolean =
    props.isImpeller &&
    props.impellerAngle !== null &&
    !isNaN(parseFloat(props.impellerAngle)) &&
    parseFloat(props.impellerAngle) > 0;
  const partHasModifiedImpellerTrim: boolean =
    props.isImpeller &&
    props.impellerTrim !== null &&
    !isNaN(parseInt(props.impellerTrim, 10)) &&
    parseInt(props.impellerTrim, 10) >= 1;

  // Get the lead time for when part(s) will be shipped.
  useApi(
    () => {
      if (props.partNumber === "" || props.quantity === "") {
        setLoading(true);
        return false;
      } else {
        setLoading(true);
        return true;
      }
    },
    {
      url: `${API}/leadtime`,
      method: "POST",
      body: { partNum: props.partNumber, quantity: props.quantity },
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (props.quantity !== "") {
        if (response.ok && responseBody) {
          // The Epicor lead time date uses the following format '2025-02-24T00:00:00.000'.
          // The date is somewhat misleading as the timezone is PST/PDT and the timestamp is always 00:00:00.000.
          // This means that we have to adjust the timestamp if we want to keep track of a UTC date.
          // Since PST/PDT is -8/-7 hours from UTC and the timestamp always represents a start of a date (00:00:00.000),
          // we first determine if we are currently in PST or PDT and add the appropriate amount of hours.
          let epicorDateAvailable = responseBody.dateAvailable;
          if (isPacificDaylightTime(epicorDateAvailable)) {
            epicorDateAvailable = epicorDateAvailable.replace("T00", "T07");
          } else {
            epicorDateAvailable = epicorDateAvailable.replace("T00", "T08");
          }
          calculateLeadTime(
            epicorDateAvailable,
            partHasPaintColor,
            partHasModifiedImpellerAngle,
            partHasModifiedImpellerTrim
          );
          setFailedToLoad(false);
        } else {
          setFailedToLoad(true);
        }
      }
      setLoading(false);
    },
    [props.partNumber, props.quantity, props.paintColorId, props.impellerAngle, props.impellerTrim]
  );

  // Calculate a part's lead time based on selected modifications.
  async function calculateLeadTime(
    dateAvailable: string,
    partHasPaintColor: boolean,
    partHasModifiedImpellerAngle: boolean,
    partHasModifiedImpellerTrim: boolean
  ) {
    let dateUtc: string = dateAvailable;
    // Always add one day of time to the date available per the sales team's instructions.
    dateUtc = isoUtcAddDays(dateUtc, 1);
    // If a paint color is selected, add 1 day to lead time.
    if (partHasPaintColor) {
      dateUtc = isoUtcAddDays(dateUtc, 1);
    }
    // If there are impeller modifications, add 2 days to lead time.
    if (partHasModifiedImpellerAngle || partHasModifiedImpellerTrim) {
      dateUtc = isoUtcAddDays(dateUtc, 2);
    }
    dateUtc = addBusinessDays(dateUtc);
    setLeadTime(isoToTimePassed(dateUtc));
    props.onLeadTimeCalculated(dateUtc);
  }

  return failedToLoad ? (
    <div>Unable to calculate lead time.</div>
  ) : (
    <div className={`${styles.row} row`}>
      <div data-test="lead-time" className="col-12">
        {loading ? <DotLoader loading={loading} className={props.className} /> : <span>{leadTime}</span>}
      </div>
    </div>
  );
}

interface Props {
  partNumber: string;
  quantity: string;
  paintColorId: number | null;
  isImpeller: boolean;
  impellerTrim: string | null;
  impellerAngle: string | null;
  className: string;
  onLeadTimeCalculated: (dateAvailable: string) => void;
}

interface ResponseBody {
  dateAvailable: string;
}
